/**
 * Date parsing and formatting.
 *
 * The ledger stores dates as display strings ("05 May 2026"), because that is
 * what the spreadsheets people import look like. `parseDate` is deliberately
 * forgiving: it accepts that format plus ISO, dd/mm/yyyy, and anything Date
 * itself can read, and returns null rather than an Invalid Date.
 */
export const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
export const NO_TRIAL = 'No trial';

export const pad2 = n => String(n).padStart(2, '0');

/**
 * Apply an am/pm suffix to an hour read off a 12-hour clock.
 *
 * "12:30 am" is half past midnight and "12:30 pm" is half past noon, which is
 * the one case a naive `+12` gets backwards.
 */
function withMeridiem(hour, s) {
  const m = /\b(a|p)\.?m\.?\s*$/i.exec(s);
  if (!m) return hour;
  const pm = m[1].toLowerCase() === 'p';
  if (pm) return hour === 12 ? 12 : hour + 12;
  return hour === 12 ? 0 : hour;
}

export function parseDate(v) {
  if (!v) return null;
  const s = String(v).trim();
  if (!s || s === '-' || s === '—' || s.toLowerCase() === NO_TRIAL.toLowerCase() || s.toLowerCase() === 'n/a') return null;

  let m = s.match(/^(\d{1,2})[\s\-/]([A-Za-z]{3,})[\s\-/](\d{4})(?:\s+(\d{1,2}):(\d{2}))?/);
  if (m) {
    const mo = MONTHS.indexOf(m[2].slice(0, 3).toLowerCase());
    if (mo > -1) return new Date(+m[3], mo, +m[1], withMeridiem(+(m[4] || 0), s), +(m[5] || 0));
  }
  m = s.match(/^(\d{4})-(\d{2})-(\d{2})(?:[\sT](\d{1,2}):(\d{2}))?/);
  if (m) {
    /*
     * A trailing Z or offset means the string is an instant, not a wall clock.
     *
     * Everything else here is a wall clock — "2026-09-22" is that day
     * wherever you are reading it, and a due date does not move when you fly
     * to London. But "2026-09-22T14:31:59.561Z" is a moment, and reading its
     * digits as local time made it four hours earlier than it happened. The
     * visitor tracker stamps every visit that way, so a lead that came in at
     * half six in the evening was showing as half two.
     */
    if (/(Z|[+-]\d{2}:?\d{2})$/.test(s)) {
      const at = new Date(s);
      if (!Number.isNaN(at.getTime())) return at;
    }
    return new Date(+m[1], +m[2] - 1, +m[3], withMeridiem(+(m[4] || 0), s), +(m[5] || 0));
  }
  m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{1,2}):(\d{2}))?/);
  if (m) return new Date(+m[3], +m[2] - 1, +m[1], withMeridiem(+(m[4] || 0), s), +(m[5] || 0));

  const d = new Date(s);
  return isNaN(d) ? null : d;
}

/** Format for display, honouring the workspace's `dateFmt` preference. */
export function formatDate(d, withTime, dateFmt = 'long') {
  if (!d) return '';
  /*
   * An invalid Date used to take the page down with it.
   *
   * getMonth() on one returns NaN, MONTHS[NaN] is undefined, and .replace
   * on undefined throws — so `new Date('No trial')` anywhere in the app
   * unmounted whatever was drawing it. The ledger really does store "No
   * trial" in a date field, so this is not a hypothetical. Nothing is
   * better than a white screen.
   */
  if (Number.isNaN(d.getTime?.() ?? NaN)) return '';
  const base =
    dateFmt === 'iso' ? `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`
      : dateFmt === 'slash' ? `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`
        : `${pad2(d.getDate())} ${MONTHS[d.getMonth()].replace(/^./, c => c.toUpperCase())} ${d.getFullYear()}`;
  if (!withTime) return base;
  /*
   * Twelve-hour with am/pm.
   *
   * It was `14:31`, which nobody in this business writes or says — the
   * visitor tracker's own dashboard reads "01:21:29 pm" and the team reads
   * that. Worse, `10:52` with no meridiem is genuinely ambiguous on a board
   * where enquiries arrive round the clock: there is no way to tell a lead
   * that came in before breakfast from one that came in at night.
   *
   * parseDate reads the meridiem back, which it has to: the campaign
   * scheduler round-trips through this pair, and a send set for nine in the
   * evening going out at nine in the morning is the bug this would otherwise
   * have introduced.
   */
  const h = d.getHours();
  const h12 = h % 12 || 12;
  return `${base} ${pad2(h12)}:${pad2(d.getMinutes())} ${h < 12 ? 'am' : 'pm'}`;
}

export const startOfDay = d => new Date(d.getFullYear(), d.getMonth(), d.getDate());
/** Whole days from today to `d` — negative when `d` is in the past. */
export const daysTo = d => (d ? Math.round((startOfDay(d) - startOfDay(new Date())) / 864e5) : null);
export const daysAgo = d => (d ? -daysTo(d) : null);
export const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };

/** Advance one billing period. Months are calendar months, not 30-day blocks. */
export function addPeriod(d, cycle) {
  const x = new Date(d);
  const c = String(cycle || '').toLowerCase();
  if (c === 'annual') x.setFullYear(x.getFullYear() + 1);
  else if (c === 'half-yearly') x.setMonth(x.getMonth() + 6);
  else if (c === 'quarterly') x.setMonth(x.getMonth() + 3);
  else x.setMonth(x.getMonth() + 1);
  return x;
}

export const periodDays = c => {
  const s = String(c || '').toLowerCase();
  return s === 'annual' ? 365 : s === 'half-yearly' ? 182 : s === 'quarterly' ? 91 : s === 'monthly' ? 30 : 0;
};

export const periodLabel = c => {
  const s = String(c || '').toLowerCase();
  return s === 'annual' ? '/yr' : s === 'half-yearly' ? '/6mo' : s === 'quarterly' ? '/qtr' : s === 'monthly' ? '/mo' : '';
};

/**
 * The same thing in words, for under a figure rather than beside it.
 *
 * "AED 950" with "a month" under it was wrong about an annual account by a
 * factor of twelve, and it read as a fact rather than a label.
 */
export const periodWord = c => {
  const s = String(c || '').toLowerCase();
  return s === 'annual' ? 'a year' : s === 'half-yearly' ? 'six months'
    : s === 'quarterly' ? 'a quarter' : 'a month';
};

/**
 * The two halves of a native date input.
 *
 * A date field stored as "19 Oct 2026" cannot go into <input type="date">,
 * which speaks only yyyy-mm-dd, and what it hands back cannot go straight
 * into a record that everything else reads with the org's own format. So
 * the value is converted on the way in and on the way out, and the stored
 * shape never changes.
 */
export const toInputDate = v => {
  const d = parseDate(v);
  return d ? `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}` : '';
};

/** And back, in whatever format the org writes dates in. */
export const fromInputDate = (v, dateFmt) => {
  const d = parseDate(v);
  return d ? formatDate(d, false, dateFmt) : '';
};
